import * as React from 'react'

import {
  Block,
  getBlockUpdateOverride,
  getValueBasedOnSelectedVariants,
  type DateBlock as TDateBlock,
} from '@basehub/data/replicache/index'

import { useRepoReplicache } from '~/hooks-data/use-repo-replicache'
import { Input, InputProps } from '~/sotano-kit/primitives/input'
import { useSyncedValue } from '~/hooks/use-synced-value'
import { useBlockInputBaseProps } from '../block-wrapper'
import { HTMLInputToIsoString, isoStringToHTMLInput } from '~/lib/date-utils'
import { useDeviceDetect } from '~/hooks/use-device-detect'
import { useSelectedVariants } from '~/hooks/use-selected-variants'
import { useRepoUndoManager } from '~/replicache/frontend/undo-redo/manager'
import { idToSourceBlockId } from '@basehub/data/replicache/mutators/repo-space/block/mutators/copy-on-write'
import { UpdateBlockParams } from '@basehub/data/replicache/mutators/repo-space/block/mutators/crud'
import { useTabsStore } from '../../tabs/store'
import { useEditorMode } from '../../editor-mode-provider'
import { clsx } from 'clsx'
import { CalendarIcon } from '@radix-ui/react-icons'

export const DateBlockInput = ({
  block,
  parent,
  path,
  readOnly,
  ...props
}: {
  block: TDateBlock
  parent: Block
  path: string
} & InputProps) => {
  const { data } = useRepoReplicache()
  const replicache = data?.replicache
  const undoManager = useRepoUndoManager()
  const activeTabKey = useTabsStore((s) => s.activeTab?.key)
  const editorMode = useEditorMode()
  const selectedDashboardVariants = useSelectedVariants({
    parentBlock: parent,
    block,
  })

  const _value: string = React.useMemo(() => {
    const v = getValueBasedOnSelectedVariants({
      block,
      variantsToFilterBy: selectedDashboardVariants,
    })

    return !v
      ? ''
      : isoStringToHTMLInput(v, { useTime: block.typeOptions.useTime })
  }, [block, selectedDashboardVariants])

  const {
    onChange,
    sync: _,
    valueRef: __,
    ...syncedInputProps
  } = useSyncedValue<HTMLInputElement>({
    value: _value,
    customEventsKey: `synced-value:date:${idToSourceBlockId(block)}`,
    setValue: React.useCallback(
      async (v, { internalValueRef, customEventsKey }) => {
        if (!replicache?.mutate) return
        const getParams = (v: string): UpdateBlockParams => ({
          id: block.id,
          instanceChildTarget: block.instanceChildTarget,
          data: getBlockUpdateOverride({
            block,
            newValue: HTMLInputToIsoString(v),
            selectedVariants: selectedDashboardVariants,
          }),
          path,
          strategy: 'merge-deep',
          isoNow: new Date().toISOString(),
        })
        const { undo } = await replicache.mutate.updateBlock(getParams(v))
        if (undo) {
          undoManager.current?.push({
            redo: [
              {
                op: 'focus',
                args: {
                  preferredTabKey: activeTabKey,
                  focus: {
                    type: 'block:value',
                    blockId: idToSourceBlockId({
                      id: block.id,
                      sourceBlockId: block.sourceBlockId,
                    }),
                  },
                },
              },
              { op: 'updateBlock', args: getParams(internalValueRef.current) },
              {
                op: 'customWindowEvent',
                args: { name: customEventsKey, detail: { action: 'sync' } },
              },
            ],
            undo: [
              {
                op: 'focus',
                args: {
                  preferredTabKey: activeTabKey,
                  focus: {
                    type: 'block:value',
                    blockId: idToSourceBlockId({
                      id: block.id,
                      sourceBlockId: block.sourceBlockId,
                    }),
                  },
                },
              },
              { op: 'blockBatchMutations', args: { mutations: undo } },
              {
                op: 'customWindowEvent',
                args: { name: customEventsKey, detail: { action: 'sync' } },
              },
            ],
          })
        }
      },
      [
        replicache?.mutate,
        block,
        selectedDashboardVariants,
        path,
        undoManager,
        activeTabKey,
      ]
    ),
  })

  const baseProps = useBlockInputBaseProps(block)
  const { isSafari } = useDeviceDetect()
  const isReadOnly = readOnly || editorMode === 'read'

  return (
    <div className="relative">
      <Input
        {...baseProps}
        ref={syncedInputProps.ref}
        value={syncedInputProps.value}
        {...props}
        readOnly={isReadOnly}
        max="9999-12-31"
        className={clsx('[&::-webkit-calendar-picker-indicator]:hidden')}
        onChange={(e) => onChange(e.target.value)}
      />
      {isSafari && !syncedInputProps.value && (
        // safari's empty state defaults to today, which is super odd. we clarify the input is empty.
        <span
          className="absolute top-1/2 select-none pointer-events-none -translate-y-1/2 text-xs text-dark-faint"
          style={{
            left: block.typeOptions.useTime ? '152px' : '104px',
          }}
        >
          (empty)
        </span>
      )}
      {!readOnly && (
        <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-1.5">
          <button
            className="cursor-default text-dark-faint hover:text-dark-dim transition-colors text-xs font-medium"
            onClick={() => {
              const now = new Date()
              const year = now.getFullYear()
              const month = String(now.getMonth() + 1).padStart(2, '0')
              const day = String(now.getDate()).padStart(2, '0')

              const todayISO = block.typeOptions.useTime
                ? now.toISOString()
                : new Date(`${year}-${month}-${day}`).toISOString()
              onChange(
                isoStringToHTMLInput(todayISO, {
                  useTime: block.typeOptions.useTime,
                })
              )
              syncedInputProps.ref.current?.focus()
            }}
          >
            <span>Set to Today</span>
          </button>
          <button
            onClick={() => {
              // i add focus here because in safari, if the input is not fucused, the picker can't be toggled when clicking the button
              syncedInputProps.ref.current?.focus()
              syncedInputProps.ref.current?.showPicker()
            }}
            className="text-dark-faint hover:text-dark-dim transition-colors"
          >
            <CalendarIcon className="size-3.5" />
          </button>
        </div>
      )}
    </div>
  )
}
